import React, { useEffect, useState, useCallback } from "react";
import PrincipalLeadsHeader from "../components/PrincipalLeadsHeader";
import VendedorTable from "../../Vendedor/components/VendedorTable";
import ReassignDialog from "../components/ReassignDialog";
import ClienteDialog from "../../Vendedor/components/ClienteDialog";
import SeguimientoContainer from "../../Vendedor/containers/SeguimientoContainer";
import socket from "../../socket";
import { getUserContext } from "../../auth";
import { apiFetch } from "../../api";
import { Box } from "@mui/material";
import DuplicateResolveDialog from "../components/DuplicateResolveDialog";
import EditConflictTransferDialog from "../components/EditConflictTransferDialog";

const EXCLUSIVE_FILTERS = {
  TOTAL: "TOTAL",
  NUEVOS: "NUEVOS",
  ATRASADOS: "ATRASADOS",
};


const UI = {
  ESTADIOS: [
    { key: "INICIO", label: "Inicio", color: "#d9ebfd" },
    { key: "INFORMACION", label: "Información", color: "#cfeefd" },
    { key: "PRECIO", label: "Precio", color: "#daf5f8" },
    { key: "NEGOCIACION", label: "Negociación", color: "#fffbd4" },
    { key: "SEGUIMIENTO", label: "Seguimiento", color: "#fff1c8" },
    { key: "INTERES", label: "Interés", color: "#fee8c7" },
  ],
  ESTADOS: ["ACTIVO", "VENTA", "BAJA", "FUTURO"],
  COLORS: { vendedorHeader: "#f7f7f9", estadoHeader: "#e0eed7", derechaBg: "#f6f7f9", bodyBg: "#fff" },
  WIDTHS: { estadioCol: 100, vendedorCol: 124 },
};

export default function PrincipalTableContainer({ isFullScreen, onToggleFullScreen, headerActions = null }) {
  const [rows, setRows] = useState([]);
  const [visibleRows, setVisibleRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Paginación
  const [page, setPage] = useState(0); // Material UI usa base 0
  const [limit, setLimit] = useState(15);

  // Header: conteo de leads
  const [leads, setLeads] = useState({ total: 0, nuevos: 0, atrasados: 0 });

  // Filtro por vendedor
  const [vendedores, setVendedores] = useState([]);
  const [vendedoresLoading, setVendedoresLoading] = useState(false);
  const [selectedVendedorId, setSelectedVendedorId] = useState(""); // "" = todos

  // Filtro exclusivo (Totales / Nuevos / Atrasados)
  const [exclusiveFilter, setExclusiveFilter] = useState(EXCLUSIVE_FILTERS.TOTAL);

  // Modales
  const [openClient, setOpenClient] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientSaving, setClientSaving] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicatePayload, setDuplicatePayload] = useState(null);
  const [editConflictOpen, setEditConflictOpen] = useState(false);
  const [editConflictPayload, setEditConflictPayload] = useState(null);
  const [openSeg, setOpenSeg] = useState(false);
  const [segAsign, setSegAsign] = useState(null);
  const [openReassign, setOpenReassign] = useState(false);
  const [reassignAsign, setReassignAsign] = useState(null);
  const [reassignShouldReopen, setReassignShouldReopen] = useState(false);
  const [reassignHelperText, setReassignHelperText] = useState('');
  const [reassignNotifId, setReassignNotifId] = useState(null);

  // Cuando cambia isFullScreen, ajustamos el límite y reseteamos página
  useEffect(() => {
    if (isFullScreen) {
      setLimit(100);
    } else {
      setLimit(15);
    }
    setPage(0); // Volver a la primera página
  }, [isFullScreen]);

  // Cargar vendedores para el filtro (solo activos)
  const loadVendedores = useCallback(async () => {
    setVendedoresLoading(true);
    try {
      const r = await apiFetch(`/api/vendedores/listado?ocultar_inactivos=true&limit=1000`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const lista = Array.isArray(data) ? data : data.items || [];
      const activos = lista.filter(v => v.activo !== false);
      setVendedores(activos);
    } catch (e) {
      console.error("Error cargando vendedores:", e);
      setVendedores([]);
    } finally {
      setVendedoresLoading(false);
    }
  }, []);

  // Conteos (soporta id_vendedor si el backend lo admite; si no, lo ignora)
  const reloadLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ estados: "ACTIVO" });
      if (selectedVendedorId) params.set("id_vendedor", String(selectedVendedorId));
      const r = await apiFetch(`/api/leads?${params.toString()}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setLeads({
        total: data?.total ?? 0,
        nuevos: data?.nuevos ?? 0,
        atrasados: data?.atrasados ?? 0,
      });
    } catch (e) {
      console.error(e);
      setLeads({ total: 0, nuevos: 0, atrasados: 0 });
    }
  }, [selectedVendedorId]);

  // Tabla de asignaciones PAGINADA
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ estados: "ACTIVO" });
      if (selectedVendedorId) params.set("id_vendedor", String(selectedVendedorId));
      if (exclusiveFilter === EXCLUSIVE_FILTERS.NUEVOS) params.set("tipo_filtro", "NUEVOS");
      if (exclusiveFilter === EXCLUSIVE_FILTERS.ATRASADOS) params.set("tipo_filtro", "ATRASADOS");

      // Paginación (API base 1)
      params.set("page", String(page + 1));
      params.set("limit", String(limit));

      const res = await apiFetch(`/api/asignaciones?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      // La respuesta ahora es { items: [...], total: N }
      setRows(Array.isArray(data.items) ? data.items : []);
      setVisibleRows(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setExclusiveFilter((prev) => prev);

    } catch (e) {
      setError(`Error al cargar las asignaciones: ${e.message}`);
      setRows([]);
      setVisibleRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVendedorId, exclusiveFilter, page, limit]);

  // Handlers de Paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  // actualiza el estado local del cliente cuando tipeás en los inputs
  const handleChangeClient = (next) => setClientData(next);

  // guarda en backend y refresca el nombre en la tabla (si cambió)
  const handleSaveClient = async (cliente) => {
    if (!cliente || !cliente.id) return null;
    setClientSaving(true);
    try {
      const payload = {
        nombre: cliente.nombre,
        telefono: cliente.telefono || null,
        email: cliente.email || null,
        obtenido_de: cliente.obtenido_de || null,
        observacion: cliente.observacion || null,
      };

      const res = await apiFetch(`/api/clientes/${cliente.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 409) {
          let json = null;
          try { json = await res.json(); } catch (e) { json = null; }

          if (json && json.origin === 'edit') {
            setEditConflictPayload(json);
            setEditConflictOpen(true);
          } else {
            setDuplicatePayload(json);
            setDuplicateOpen(true);
          }

          setClientSaving(false);
          return null;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const updated = await res.json();

      // reflejar cambios en la tabla (nombre del cliente, etc.)
      setRows(prev =>
        prev.map(r => {
          const cid = r?.id_cliente ?? r?.cliente?.id ?? r?.cliente_id ?? r?.clienteId;
          if (String(cid) === String(updated.id)) {
            return {
              ...r,
              clienteNombre: updated.nombre ?? r.clienteNombre,
              nombreCliente: updated.nombre ?? r.nombreCliente,
            };
          }
          return r;
        })
      );

      setClientData(updated);
      return updated;
    } catch (e) {
      console.error("Error guardando cliente:", e);
      throw e;
    } finally {
      setClientSaving(false);
    }
  };


  // Carga inicial
  useEffect(() => {
    loadVendedores();
  }, [loadVendedores]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const ven_clien_id = detail?.ven_clien_id;
      if (!ven_clien_id) return;

      const shouldReopen = Boolean(detail?.shouldReopen || detail?.reopenAfterReassign);
      const found = rows.find((r) => String((r.id_ven_cli ?? r.id)) === String(ven_clien_id));
      const row = found || {
        id_ven_cli: ven_clien_id,
        cliente_id: detail?.cliente_id,
        vendedor_activo: false,
        vendedor_id: detail?.vendedor_id ?? null,
      };
      const vendedorName = detail?.vendedor_nombre || found?.vendedor_nombre || '';
      const helperText = vendedorName
        ? `El vendedor ${vendedorName} asignado a este cliente no está más activo. Seleccioná otro vendedor para continuar.`
        : 'El vendedor asignado a este cliente no está más activo. Seleccioná otro vendedor para continuar.';

      setReassignAsign(row);
      setReassignShouldReopen(shouldReopen);
      setReassignHelperText(helperText);
      setReassignNotifId(detail?.notif_id ?? null);
      setOpenReassign(true);
    };

    window.addEventListener('open_reassign_for_ven_clien', handler);
    return () => window.removeEventListener('open_reassign_for_ven_clien', handler);
  }, [rows]);

  // Cargar datos cuando entra o cambia el filtro/página
  useEffect(() => {
    reloadLeads();
    reload();
  }, [reloadLeads, reload]);

  useEffect(() => {
    setPage(0);
  }, [exclusiveFilter, selectedVendedorId]);

  // Sockets: volver a cargar respetando el filtro actual
  useEffect(() => {
    const reloadData = () => {
      reloadLeads();
      reload();
    };
  
    const onCreateAsignacion = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      reloadData();
    };
  
    const onUpdateEstadoLead = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      reloadData();
    };
  
    const onChangeEstadio = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      reloadData();
    };
  
    const onCreateDevolucion = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      reloadData();
    
      const currentIdVenCli = segAsign?.id_ven_cli || segAsign?.id;
      if (openSeg && segAsign && data.id_ven_cli && currentIdVenCli === data.id_ven_cli) {
        setSegAsign(prev => ({ ...prev, _updateTrigger: Date.now() }));
      }
    };
  
    const onToggleChecklist = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      reloadData();
    
      const currentIdVenCli = segAsign?.id_ven_cli || segAsign?.id;
      if (openSeg && segAsign && data.id_ven_cli && currentIdVenCli === data.id_ven_cli) {
        setSegAsign(prev => ({ ...prev, _updateTrigger: Date.now() }));
      }
    };
  
    const onCreateCliente = (data) => {
      try {
        const user = getUserContext();
        if (String(data.empresa_id) !== String(user.empresa_id)) return;
      } catch {}
      reloadData();
    };
  
    socket.on("create_asignacion", onCreateAsignacion);
    socket.on("update_estado_lead", onUpdateEstadoLead);
    socket.on("change_estadio", onChangeEstadio);
    socket.on("create_devolucion", onCreateDevolucion);
    socket.on("toggle_checklist_field", onToggleChecklist);
    socket.on("create_cliente", onCreateCliente);
    socket.on("update_cliente", onCreateCliente);
    socket.on("delete_cliente", onCreateCliente);
  
    return () => {
      socket.off("create_asignacion", onCreateAsignacion);
      socket.off("update_estado_lead", onUpdateEstadoLead);
      socket.off("change_estadio", onChangeEstadio);
      socket.off("create_devolucion", onCreateDevolucion);
      socket.off("toggle_checklist_field", onToggleChecklist);
      socket.off("create_cliente", onCreateCliente);
      socket.off("update_cliente", onCreateCliente);
      socket.off("delete_cliente", onCreateCliente);
    };
  }, [reload, reloadLeads, openSeg, segAsign]);


  const handleOpenCliente = async (row) => {
    const clienteId =
      row?.id_cliente ?? row?.cliente?.id ?? row?.cliente_id ?? row?.clienteId;
    if (!clienteId) return;
    setClientLoading(true);
    setOpenClient(true);
    setClientData(null);
    try {
      const r = await apiFetch(`/api/clientes/${clienteId}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setClientData(data);
    } catch (e) {
      setClientData(null);
    } finally {
      setClientLoading(false);
    }
  };

  const handleOpenSeguimiento = (row) => {
    setSegAsign(row);
    setOpenSeg(true);
  };

  const handleOpenReassign = (row, shouldReopen = false, helperText = '', notifId = null) => {
    setReassignAsign(row);
    setReassignShouldReopen(Boolean(shouldReopen));
    setReassignHelperText(helperText);
    setReassignNotifId(notifId);
    setOpenReassign(true);
  };

  const handleCloseReassign = () => {
    setReassignAsign(null);
    setOpenReassign(false);
    setReassignShouldReopen(false);
    setReassignHelperText('');
    setReassignNotifId(null);
  };

  const handleDoReassign = async (newVendedorId) => {
    if (!reassignAsign) return;
    const id_ven_cli = reassignAsign.id_ven_cli ?? reassignAsign.id;
    const cliente_id = reassignAsign.id_cliente ?? reassignAsign.cliente_id ?? reassignAsign.clienteId ?? reassignAsign.cliente?.id;
    const vendedor_anterior_id = reassignAsign.id_vendedor ?? reassignAsign.vendedor_id ?? reassignAsign.vendedorAnteriorId;

    if (!id_ven_cli || !cliente_id || !vendedor_anterior_id || !newVendedorId) {
      throw new Error('No se pudo preparar la reasignación: faltan datos del cliente o del vendedor.');
    }

    try {
      const payload = {
        id_nuevo_vendedor: Number(newVendedorId),
        vendedor_anterior_id: Number(vendedor_anterior_id),
        cliente_id: Number(cliente_id),
      };
      const endpoint = reassignShouldReopen || reassignNotifId
        ? `/api/asignaciones/${id_ven_cli}/reasignar-y-reactivar`
        : `/api/asignaciones/${id_ven_cli}/reasignar`;
      const res = await apiFetch(endpoint, {
        method: reassignShouldReopen || reassignNotifId ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          notif_id: reassignNotifId || null,
        }),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = Array.isArray(err.detail)
          ? err.detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(', ')
          : (typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail || {}));
        throw new Error(detail || `HTTP ${res.status}`);
      }
      await reload();
      await reloadLeads();
      return true;
    } catch (e) {
      console.error('Error reasignando:', e);
      alert(`No se pudo reasignar: ${e.message || 'error desconocido'}`);
      return false;
    }
  };

  const handleReassignFromTable = async (row, newVendedorId) => {
    if (!row || !newVendedorId) return false;

    const id_ven_cli = row.id_ven_cli ?? row.id;
    const cliente_id = row.id_cliente ?? row.cliente_id ?? row.clienteId ?? row.cliente?.id;
    const vendedor_anterior_id = row.id_vendedor ?? row.vendedor_id ?? row.vendedorAnteriorId;

    if (!id_ven_cli || !cliente_id || !vendedor_anterior_id || String(vendedor_anterior_id) === String(newVendedorId)) {
      return false;
    }

    try {
      const res = await apiFetch(`/api/asignaciones/${id_ven_cli}/reasignar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_nuevo_vendedor: Number(newVendedorId),
          vendedor_anterior_id: Number(vendedor_anterior_id),
          cliente_id: Number(cliente_id),
        }),
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = Array.isArray(err.detail)
          ? err.detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(', ')
          : (typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail || {}));
        throw new Error(detail || `HTTP ${res.status}`);
      }

      await reload();
      await reloadLeads();
      return true;
    } catch (e) {
      console.error('Error reasignando desde tabla:', e);
      alert(`No se pudo reasignar: ${e.message || 'error desconocido'}`);
      return false;
    }
  };

  const tableHeightMode = isFullScreen ? "flex" : undefined;

  return (
    <>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        gap: isFullScreen ? 0 : 2,
        height: '100%',
        minHeight: 0
      }}>
        <PrincipalLeadsHeader
          total={leads.total}
          nuevos={leads.nuevos}
          atrasados={leads.atrasados}
          vendedores={vendedores}
          vendedoresLoading={vendedoresLoading}
          selectedVendedorId={selectedVendedorId}
          onChangeVendedor={(val) => {
            setSelectedVendedorId(val);
            setPage(0);
          }}
          exclusiveFilter={exclusiveFilter}
          onChangeExclusiveFilter={(next) => {
            setExclusiveFilter(next);
            setPage(0);
          }}
          isFullScreen={isFullScreen}
          onToggleFullScreen={onToggleFullScreen}
          actions={headerActions}
        />

        <VendedorTable
          rows={visibleRows}
          loading={loading}
          error={error}
          ui={UI}
          readOnly={true}
          estadoDisabled={true}
          showVendedorColumn={true}
          vendedores={vendedores}
          onSelectAssignment={() => { }}
          onEstadoChange={() => { }}
          onOpenSeguimiento={handleOpenSeguimiento}
          onDropToEstadio={() => { }}
          onOpenCliente={handleOpenCliente}
          onOpenReassign={handleOpenReassign}
          onReassignVendedor={handleReassignFromTable}
          customHeight={tableHeightMode}
          count={total}
          page={page}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* Cliente — solo ver */}
        <ClienteDialog
          open={openClient}
          onClose={() => setOpenClient(false)}
          cliente={clientData}
          onChange={handleChangeClient}
          onSave={handleSaveClient}
          onOpenSeguimiento={handleOpenSeguimiento}
          saving={clientSaving}
          loading={clientLoading}
        />

        <DuplicateResolveDialog
          open={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          existing={duplicatePayload?.existing}
          incoming={duplicatePayload?.incoming}
          match_source={duplicatePayload?.match_source}
          onResolved={({ kept, created }) => {
            setDuplicateOpen(false);
            try { reload(); } catch (e) { /* ignore */ }
            setOpenClient(false);
          }}
        />

        <EditConflictTransferDialog
          open={editConflictOpen}
          onClose={() => { setEditConflictOpen(false); setEditConflictPayload(null); }}
          existing={editConflictPayload?.existing}
          incoming={editConflictPayload?.incoming}
          resolution={editConflictPayload?.resolution}
          target={clientData}
          onResolved={({ kept, created }) => {
            setEditConflictOpen(false);
            setEditConflictPayload(null);
            try { reload(); } catch (e) { /* ignore */ }
            setOpenClient(false);
          }}
        />

        <SeguimientoContainer
          open={Boolean(openSeg && segAsign)}
          onClose={() => { setOpenSeg(false); setSegAsign(null); }}
          asignacion={segAsign ?? null}
          adminReadOnly={true}
        />

        <ReassignDialog
          open={openReassign}
          onClose={handleCloseReassign}
          vendedores={vendedores}
          asignacion={reassignAsign}
          onReassign={handleDoReassign}
          helperText={reassignHelperText}
        />
      </Box>
    </>
  );
}
